import React, { memo } from 'react';
import {
  Box,
  CircularProgress,
  Typography,
  Fade,
  keyframes
} from '@mui/material';

// Custom animation for a more polished loading effect
const pulseAnimation = keyframes`
  0% {
    box-shadow: 0 2px 8px rgba(0,0,0,0.12);
  }
  50% {
    box-shadow: 0 4px 16px rgba(0,0,0,0.16);
  }
  100% {
    box-shadow: 0 2px 8px rgba(0,0,0,0.12);
  }
`;

/**
 * Reusable loading indicator component with consistent design
 * Can be used for search, sorting, and other async operations
 */
const LoadingIndicator = memo(({
  isLoading = false,
  message = 'Loading...',
  size = 'small',
  position = 'inline', // 'inline', 'floating', 'overlay'
  color = 'var(--mainColor)',
  className = '',
  style = {}
}) => {
  if (!isLoading) return null;

  const sizeMap = {
    small: 16,
    medium: 24,
    large: 32
  };

  const progressSize = sizeMap[size] || sizeMap.small;

  const baseStyles = {
    display: 'flex',
    alignItems: 'center',
    gap: 1,
    ...style
  };

  const positionStyles = {
    inline: {
      ...baseStyles
    },
    floating: {
      ...baseStyles,
      position: 'absolute',
      right: 16,
      top: '50%',
      transform: 'translateY(-50%)',
      zIndex: 10,
      backgroundColor: 'white',
      padding: '8px 14px',
      borderRadius: 3,
      border: '1px solid #e0e0e0',
      animation: `${pulseAnimation} 2s ease-in-out infinite`,
      backdropFilter: 'blur(4px)',
      background: 'linear-gradient(145deg, #ffffff 0%, #f8f9fa 100%)'
    },
    overlay: {
      ...baseStyles,
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(255, 255, 255, 0.9)',
      backdropFilter: 'blur(2px)',
      zIndex: 100,
      justifyContent: 'center'
    }
  };

  return (
    <Fade in={isLoading} timeout={200}>
      <Box 
        className={className}
        sx={positionStyles[position]}
      >
        <CircularProgress 
          size={progressSize} 
          thickness={4.5}
          sx={{ 
            color,
            '& .MuiCircularProgress-circle': {
              strokeLinecap: 'round',
            },
            '& .MuiCircularProgress-svg': {
              filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.1))'
            }
          }} 
        />
        <Typography 
          variant={size === 'large' ? 'body2' : 'caption'} 
          sx={{ 
            color: '#666',
            fontWeight: 500,
            userSelect: 'none'
          }}
        >
          {message}
        </Typography>
      </Box>
    </Fade>
  );
});

LoadingIndicator.displayName = 'LoadingIndicator';

export default LoadingIndicator;